import { apiClient } from './api.client';
import { 
  Alarm, 
  AlarmPollStatus, 
  AlarmFilters, 
  AlarmListResponse,
  AlarmTestResult 
} from '../types/alarm.types';

/**
 * HikVision Alarm Service
 * 
 * Service for managing HikVision cloud alarm polling
 */
class AlarmService {
  private readonly BASE_PATH = '/camera-security/locations';

  /**
   * Get alarms for a location
   */
  async getLocationAlarms(
    locationId: number, 
    filters?: AlarmFilters
  ): Promise<AlarmListResponse> {
    const params: Record<string, any> = {
      page: filters?.page || 1,
      page_size: filters?.page_size || 50,
    };

    if (filters?.alarm_type) {
      params.alarm_type = filters.alarm_type;
    }

    if (filters?.alarm_priority) {
      params.alarm_priority = filters.alarm_priority;
    }

    if (filters?.start_date) {
      params.start_date = filters.start_date;
    }

    if (filters?.end_date) {
      params.end_date = filters.end_date;
    }

    return apiClient.get<AlarmListResponse>(
      `${this.BASE_PATH}/${locationId}/alarms`,
      params
    );
  }

  /**
   * Get alarm polling status for a location
   */
  async getAlarmPollingStatus(locationId: number, signal?: AbortSignal): Promise<AlarmPollStatus> {
    const response = await apiClient.get<{ success: boolean } & AlarmPollStatus>(
      `${this.BASE_PATH}/${locationId}/alarm-polling/status`, undefined, { signal }
    );
    return response;
  }

  /**
   * Enable alarm polling for a location
   */
  async enableAlarmPolling(locationId: number): Promise<{ success: boolean; message: string }> {
    return apiClient.post<{ success: boolean; message: string }>(
      `${this.BASE_PATH}/${locationId}/alarm-polling/enable`,
      {}
    );
  }

  /**
   * Disable alarm polling for a location
   */
  async disableAlarmPolling(locationId: number): Promise<{ success: boolean; message: string }> {
    return apiClient.post<{ success: boolean; message: string }>(
      `${this.BASE_PATH}/${locationId}/alarm-polling/disable`,
      {}
    );
  }

  /**
   * Test alarm polling connection
   */
  async testAlarmConnection(locationId: number): Promise<AlarmTestResult> {
    return apiClient.post<AlarmTestResult>(
      `${this.BASE_PATH}/${locationId}/alarm-polling/test`,
      {}
    );
  }
}

export const alarmService = new AlarmService();
export default alarmService;
